import type { MaybeRef } from "@vueuse/core";
import type { RouteLocationNormalized } from "vue-router";

type AnalyticsContextData = Record<string, unknown>;

type AnalyticsContextEntry = {
  data: MaybeRef<AnalyticsContextData>;
  resetOnRouteChange?: (
    to: RouteLocationNormalized,
    from: RouteLocationNormalized,
  ) => boolean;
};

export function defineAnalyticsContext(
  data: MaybeRef<AnalyticsContextData>,
  options?: {
    merge?: boolean;
    resetOnRouteChange?: AnalyticsContextEntry["resetOnRouteChange"];
  },
) {
  const { reset, push } = useAnalyticsContext();

  if (!options?.merge) {
    reset();
  }

  push({ data, resetOnRouteChange: options?.resetOnRouteChange });
}

export function useAnalyticsContext() {
  const state = useState<AnalyticsContextEntry[]>(
    "analytics_context",
    () => [],
  );

  const context = computed(() => {
    return state.value.reduce((_context, entry) => {
      return {
        ..._context,
        ...unref(entry.data),
      };
    }, {} as AnalyticsContextData);
  });

  function push(entry: AnalyticsContextEntry) {
    state.value.push(entry);
  }

  function reset() {
    state.value = [];
  }

  function onRouteChange(
    to: RouteLocationNormalized,
    from: RouteLocationNormalized,
  ) {
    state.value = state.value.filter(
      (entry) => !(entry.resetOnRouteChange?.(to, from) ?? true),
    );
  }

  return {
    context,
    push,
    reset,
    onRouteChange,
  };
}
